import React, { useEffect } from 'react';
import { FormattedMessage } from '../../../util/reactIntl';
import { ListingCard } from '../../../components';
import useCardDecodedSizes from '../../../hooks/useCardDecodedSizes';

import css from './NoSearchResultsMaybe.module.css';

// Empty-state newest grid: 4-up desktop, single column below 768px
const NEWEST_CARD_RENDER_SIZES = '(max-width: 767px) 100vw, 25vw';

/**
 * Empty state (design ref C1 + C3): "no horses" message, the applied filter
 * chips (removable, same handlers as the header row), a gold clear-all
 * button, and a "Newest on The Premier Horse" grid of the 16 newest active
 * listings (4×4 desktop, single column mobile). The newest fetch is lazy — it
 * fires only when this empty state actually renders; on error / zero results
 * the grid is omitted silently.
 */
const NoSearchResultsMaybe = props => {
  const {
    listingsAreLoaded,
    totalItems,
    location,
    resetAll,
    appliedFilterChips,
    newestListings = [],
    newestFetched,
    newestInProgress,
    onFetchNewestListings,
  } = props;
  // 🔴 Story 12.15 (AC-5c) — ONE decoded-size map for the whole surface. Each
  // card reports its own `<img>`'s decode into it on load; nothing is fetched.
  const { decodedSizeFor, onImageDecoded } = useCardDecodedSizes();

  const hasNoResult = listingsAreLoaded && totalItems === 0;
  const hasSearchParams = location.search?.length > 0;

  useEffect(() => {
    if (hasNoResult && !newestFetched && !newestInProgress && onFetchNewestListings) {
      onFetchNewestListings();
    }
  }, [hasNoResult, newestFetched, newestInProgress, onFetchNewestListings]);

  const showNewestRow = newestFetched && newestListings.length > 0;

  return hasNoResult ? (
    <div className={css.emptyState}>
      <h3 className={css.heading}>
        <FormattedMessage id="SearchPage.noResults" />
      </h3>
      <div className={css.goldRule} />
      <p className={css.subline}>
        <FormattedMessage id="SearchPage.noResultsSubline" />
      </p>
      {hasSearchParams && appliedFilterChips ? (
        <div className={css.chipRow}>{appliedFilterChips}</div>
      ) : null}
      {hasSearchParams ? (
        <button className={css.clearAllButton} onClick={e => resetAll(e)}>
          <FormattedMessage id="SearchPage.resetAllFilters" />
        </button>
      ) : null}
      {showNewestRow ? (
        <>
          <div className={css.newestHead}>
            <span className={css.newestEyebrow}>
              <FormattedMessage id="SearchPage.newestHeading" />
            </span>
            <span className={css.newestRule} />
          </div>
          <div className={css.newestGrid}>
            {newestListings.map(l => (
              <ListingCard
                key={l.id.uuid}
                listing={l}
                renderSizes={NEWEST_CARD_RENDER_SIZES}
                decodedSize={decodedSizeFor(l)}
                onImageDecoded={onImageDecoded}
                sourcePage="search"
              />
            ))}
          </div>
        </>
      ) : null}
    </div>
  ) : null;
};

export default NoSearchResultsMaybe;
